/**
 * The three gates that decide which Canlan fields a division dialog shows and
 * submits.
 *
 * They come straight from the legacy `canlanDivisionForm`
 * (`web/src/components/Form/form-configs.js`) and are DELIBERATELY not the same
 * condition — merging them would show fields on the wrong tenant or the wrong
 * org, and change what the API receives:
 *
 * - the registration/Salesforce block gates on the ORG flag alone
 *   (`showDivisionFields`, form-configs.js:1150) — no tenant check
 * - the Salesforce ID gates on the TENANT alone, for Season or Division
 *   (form-configs.js:1139) — no org flag
 * - the online-registration toggle needs BOTH (form-configs.js:1343)
 *
 * Kept here rather than in the modal so the rules are testable on their own and
 * the render path and the submit path cannot drift apart.
 */
export interface DivisionFieldGateArgs {
  /** The form's current Type: 'Season' | 'Conference' | 'Division'. */
  type: string
  /** The owning organization's `sync_salesforce`. */
  showSalesforceSettings: boolean
  /** Canlan tenant. */
  isCanlanTenant: boolean
}

export interface DivisionFieldGates {
  /** Division group/age/gender, day of week, deposit, team cost, is active. */
  showDivisionFields: boolean
  /** The Salesforce ID field (`import_2_id` on write, `salesforce_id` on read). */
  showSalesforceId: boolean
  showOnlineRegistration: boolean
}

export const resolveDivisionFieldGates = ({
  type,
  showSalesforceSettings,
  isCanlanTenant,
}: DivisionFieldGateArgs): DivisionFieldGates => {
  const isDivision = type === 'Division'
  const isSeason = type === 'Season'

  const showDivisionFields = isDivision && showSalesforceSettings

  return {
    showDivisionFields,
    showSalesforceId: isCanlanTenant && (isSeason || isDivision),
    showOnlineRegistration: isCanlanTenant && showDivisionFields,
  }
}
